import React, { useState, useContext, useEffect } from 'react';
import classNames from 'classnames/bind';
import styles from './AdminDashboardPage.module.css';
import { BASE_API_URL } from '~/helper/apiService';
import { ToastContext } from '~/components/context/ToastContext';

const cx = classNames.bind(styles);

function AutoInsertVehicles() {
  const [soNgay, setSoNgay] = useState('');
  const [selectedSheet, setSelectedSheet] = useState('');
  const [sheets, setSheets] = useState([]);
  const [loadingSheets, setLoadingSheets] = useState(true);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const { showToast } = useContext(ToastContext);

  // Lấy danh sách sheets
  useEffect(() => {
    const fetchSheets = async () => {
      try {
        setLoadingSheets(true);
        const response = await fetch(`${BASE_API_URL}api/sheets`, {
          credentials: 'include',
        });
        const data = await response.json();
        if (response.ok && data.success) {
          setSheets(data.sheets || []);
        } else {
          showToast(data.error || 'Lỗi khi lấy danh sách sheets', 'error');
        }
      } catch (error) {
        showToast('Lỗi kết nối: ' + error.message, 'error');
      } finally {
        setLoadingSheets(false);
      }
    };
    fetchSheets();
  }, [showToast]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!soNgay) {
      setMessage('Vui lòng nhập số ngày');
      setIsError(true);
      showToast('Vui lòng nhập số ngày', 'error');
      return;
    }

    if (!selectedSheet) {
      setMessage('Vui lòng chọn sheet');
      setIsError(true);
      showToast('Vui lòng chọn sheet', 'error');
      return;
    }

    const soNgayNum = parseInt(soNgay, 10);

    if (isNaN(soNgayNum) || soNgayNum < 1 || soNgayNum > 31) {
      setMessage('Số ngày không hợp lệ (1-31)');
      setIsError(true);
      showToast('Số ngày không hợp lệ (1-31)', 'error');
      return;
    }

    setLoading(true);
    setMessage('');
    setIsError(false);

    try {
      const response = await fetch(`${BASE_API_URL}api/revenue/auto-insert-vehicles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          soNgay: soNgayNum,
          sheetName: selectedSheet,
        }),
      });

      const data = await response.json();
      
      if (response.ok) {
        setMessage(data.message || 'Thành công!');
        setIsError(false);
        showToast(data.message || 'Đã insert số xe thành công!', 'success');
        // Reset form
        setSoNgay('');
        setSelectedSheet('');
      } else {
        setMessage(data.message || 'Có lỗi xảy ra');
        setIsError(true);
        showToast(data.message || 'Có lỗi xảy ra', 'error');
      }
    } catch (error) {
      setMessage('Lỗi kết nối: ' + error.message);
      setIsError(true);
      showToast('Lỗi kết nối: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={cx('auto-insert-section')}>
      <div className={cx('form-container')}>
        <h2 className={cx('form-title')}>Tự động Insert Số Xe</h2>
        <p className={cx('form-description')}>
          Chức năng này sẽ tự động insert số xe từ danh mục vào sheet theo từng ngày trong tháng.
          Mỗi ngày sẽ có tất cả số xe từ danh mục.
        </p>
        
        <form onSubmit={handleSubmit} className={cx('insert-form')}>
          <div className={cx('form-group')}>
            <label htmlFor="sheet" className={cx('form-label')}>
              Chọn Sheet:
            </label>
            {loadingSheets ? (
              <div className={cx('loading-text')}>Đang tải danh sách sheets...</div>
            ) : (
              <select
                id="sheet"
                value={selectedSheet}
                onChange={(e) => setSelectedSheet(e.target.value)}
                className={cx('form-input', 'form-select')}
                required
              >
                <option value="">-- Chọn sheet --</option>
                {sheets.map((sheet) => (
                  <option key={sheet.id} value={sheet.name}>
                    {sheet.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className={cx('form-group')}>
            <label htmlFor="soNgay" className={cx('form-label')}>
              Số ngày trong tháng:
            </label>
            <input
              type="number"
              id="soNgay"
              min="1"
              max="31"
              placeholder="Ví dụ: 30"
              value={soNgay}
              onChange={(e) => setSoNgay(e.target.value)}
              className={cx('form-input')}
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className={cx('submit-button', { loading: loading })}
          >
            {loading ? 'Đang xử lý...' : 'Insert Số Xe'}
          </button>
        </form>

        {message && (
          <div className={cx('message-container', { error: isError, success: !isError })}>
            <p className={cx('message-text')}>{message}</p>
          </div>
        )}

        <div className={cx('info-box')}>
          <h3 className={cx('info-title')}>Lưu ý:</h3>
          <ul className={cx('info-list')}>
            <li>Dữ liệu sẽ được insert vào cuối sheet (không ghi đè dữ liệu hiện có)</li>
            <li>Số xe được lấy từ sheet "DanhMuc" (cột B, từ dòng 2)</li>
            <li>Mỗi ngày sẽ có tất cả số xe từ danh mục</li>
            <li>Ngày chỉ là số (1, 2, 3, ..., 31) - không có tháng/năm</li>
            <li>Ví dụ: 30 số xe × 31 ngày = 930 dòng sẽ được tạo</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default AutoInsertVehicles;
